import InsightProjectStore from '@/services/insightProject';
import type { InsightProjectRow } from '@/services/insightProject';
import { conversationsRepo, repoContext, sharedProjectRows } from './conversationsRepo';
import type { Creator } from './conversationsRepo';

export type Project = {
  id: string;
  name: string;
  description?: string;
  // Who owns the project (shown as an avatar in the list).
  owner?: Creator;
  // The "common prompt" — instructions every conversation in the project inherits.
  instructions?: string;
  // Free-text knowledge fed to conversations started from the project.
  knowledge?: string;
  datasetIds: string[];
  // Shared with the workspace: every member sees it, its conversations and can edit it.
  shared?: boolean;
  // The signed-in user owns it. Others' shared projects are editable but only the owner deletes
  // one or changes whether it is shared.
  mine?: boolean;
  createdAt: number;
  updatedAt: number;
};

export type CreateProjectInput = {
  name: string;
  description?: string;
  instructions?: string;
  knowledge?: string;
  datasetIds?: string[];
  // true = public (viewable/editable by the whole workspace); default private.
  shared?: boolean;
  owner?: Creator;
};

export interface ProjectsRepo {
  list(): Promise<Project[]>; // newest updatedAt first
  get(id: string): Promise<Project | undefined>;
  create(input: CreateProjectInput): Promise<Project>;
  update(id: string, patch: Partial<Omit<Project, 'id' | 'createdAt'>>): Promise<Project>;
  remove(id: string): Promise<void>;
}

const uid = () => `p_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

const fromRow = (row: InsightProjectRow): Project => ({
  id: row.id,
  name: row.name,
  description: row.description ?? undefined,
  instructions: row.instructions ?? undefined,
  knowledge: row.knowledge ?? undefined,
  datasetIds: row.datasetIds ?? [],
  shared: !!row.shared,
  owner: { id: row.ownerId, name: row.ownerName ?? '' },
  createdAt: Date.parse(row.createdAt ?? row.lastActivityAt) || Date.now(),
  updatedAt: Date.parse(row.lastActivityAt) || Date.now(),
});

export const projectFieldsOf = (p: Partial<Project>) => ({
  ...(p.name !== undefined ? { name: p.name } : {}),
  ...(p.description !== undefined ? { description: p.description ?? null } : {}),
  ...(p.instructions !== undefined ? { instructions: p.instructions ?? null } : {}),
  ...(p.knowledge !== undefined ? { knowledge: p.knowledge ?? null } : {}),
  ...(p.datasetIds !== undefined ? { datasetIds: p.datasetIds } : {}),
  ...(p.shared !== undefined ? { shared: !!p.shared } : {}),
  ...(p.owner !== undefined ? { ownerName: p.owner?.name ?? null } : {}),
});

// --- DynamoDB-backed (InsightProject, owner-only), per user and workspace ----------------------

type Cache = { key: string; rows: Map<string, Project>; ready: Promise<void> };
let cache: Cache | null = null;

const load = async (): Promise<Cache | null> => {
  const ctx = repoContext();
  if (!ctx) return null;
  const key = `${ctx.userId}:${ctx.workspaceId}`;
  if (cache?.key !== key) {
    const next: Cache = { key, rows: new Map(), ready: Promise.resolve() };
    next.ready = (async () => {
      const [rows, shared] = await Promise.all([
        InsightProjectStore.listForOwner(ctx.userId, ctx.workspaceId),
        sharedProjectRows(),
      ]);
      rows.forEach((row) => next.rows.set(row.id, { ...fromRow(row), mine: true }));
      shared.forEach((row) => {
        const project = fromRow(row as unknown as InsightProjectRow);
        if (!next.rows.has(project.id)) next.rows.set(project.id, { ...project, mine: false });
      });
    })();
    cache = next;
    try {
      await next.ready;
    } catch (e) {
      if (cache === next) cache = null;
      throw e;
    }
    return next;
  }
  await cache.ready;
  return cache;
};

export const projectsRepo: ProjectsRepo = {
  async list() {
    const c = await load();
    return c ? [...c.rows.values()].sort((a, b) => b.updatedAt - a.updatedAt) : [];
  },
  async get(id) {
    const c = await load();
    return c?.rows.get(id);
  },
  async create(input) {
    const ctx = repoContext();
    const c = await load();
    if (!c || !ctx) throw new Error('Not signed in to a workspace');
    const now = Date.now();
    const row: Project = {
      id: uid(),
      name: input.name,
      description: input.description,
      instructions: input.instructions,
      knowledge: input.knowledge,
      datasetIds: input.datasetIds ?? [],
      shared: input.shared ?? false,
      owner: input.owner,
      createdAt: now,
      updatedAt: now,
    };
    await InsightProjectStore.create(
      row.id,
      ctx.userId,
      ctx.workspaceId,
      { ...projectFieldsOf(row), name: row.name },
      now,
    );
    const created = { ...row, mine: true };
    c.rows.set(row.id, created);
    return created;
  },
  async update(id, patch) {
    const c = await load();
    const prev = c?.rows.get(id);
    if (!c || !prev) throw new Error(`Project ${id} not found`);
    const updated: Project = { ...prev, ...patch, id, updatedAt: Date.now() };
    if (prev.mine === false) {
      // Someone else's shared project: its content, through the workspace-checked path.
      const { name, description, instructions, knowledge, datasetIds } = patch;
      await InsightProjectStore.updateShared(
        id,
        projectFieldsOf({ name, description, instructions, knowledge, datasetIds }),
      );
      c.rows.set(id, { ...updated, shared: prev.shared, owner: prev.owner });
      return c.rows.get(id) as Project;
    }
    await InsightProjectStore.update(id, projectFieldsOf(patch));
    c.rows.set(id, updated);
    return updated;
  },
  async remove(id) {
    const c = await load();
    if (!c) return;
    if (c.rows.get(id)?.mine === false)
      throw new Error('Only the owner can delete a shared project');
    // Conversations in the removed project become loose rather than orphaned.
    const conversations = await conversationsRepo.listByProject(id);
    await Promise.all(
      conversations.map((conversation) => conversationsRepo.setProject(conversation.id, null)),
    );
    await InsightProjectStore.remove(id);
    c.rows.delete(id);
  },
};
